
import { computed, defineComponent, nextTick, reactive, ref, watch } from "vue";
import {
  IDonViGeojsonFeature,
  IKetoanAdminDraft,
  IKetoanDonViInfo,
  IKetoanDonViPreview,
  IKetoanSyncPayload,
  IKetoanSyncResult,
} from "@/core/interfaces/ApiResponses";
import {
  getErrorMsg,
  swalErrNotification,
  swalNotification,
} from "@/core/helpers/utils";
import { useButtonCustom } from "@/core/hooks/use-button-custom";
import { DonViService } from "@/core/services/DonVi.service";
import BaseModal from "@/components/shared/modal/BaseModal.vue";
import ButtonCustom from "@/components/shared/buttons/ButtonCustom.vue";
import LabelCustom from "@/components/shared/label-custom/LabelCustom.vue";
import DonViMapPreview from "@/views/crafted/pages/don-vi/DonViMapPreview.vue";

type WizardStep = "preview" | "admins" | "geojson" | "importing" | "result";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const GEOCODE_REASON_LABELS: Record<string, string> = {
  geocode_failed: "Không tìm thấy địa chỉ",
  not_found: "Không tìm thấy địa chỉ",
  invalid_address: "Địa chỉ không hợp lệ",
};

function createEmptyAdmin(maDonVi = ""): IKetoanAdminDraft {
  return {
    ma_don_vi: maDonVi,
    ho_va_ten: "",
    ten_dang_nhap: "",
    mat_khau: "",
    so_dien_thoai: "",
    email: "",
  };
}

export default defineComponent({
  name: "dong-bo-ketoan-modal",

  components: {
    BaseModal,
    ButtonCustom,
    LabelCustom,
    DonViMapPreview,
  },

  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
  },

  emits: ["close", "success"],

  setup(props, { emit }) {
    const { ButtonsType, ButtonTypeColors, ButtonSizeEnums } =
      useButtonCustom();

    const step = ref<WizardStep>("preview");
    const filterMaDonVi = ref("");
    const newDonVi = ref<IKetoanDonViPreview[]>([]);
    const selectedMaDonVi = ref("");
    const syncNguoiDung = ref(true);
    const adminDraft = reactive<IKetoanAdminDraft>(createEmptyAdmin());
    const result = ref<IKetoanSyncResult | null>(null);
    const previewMessage = ref("");
    const previewError = ref("");
    const isLoadingPreview = ref(false);

    const diaChiGoc = ref("");
    const diaChi = ref("");
    const geojsonFeature = ref<IDonViGeojsonFeature | null>(null);
    const geojsonError = ref("");
    const isLoadingGeojson = ref(false);
    const suppressDiaChiUnload = ref(false);

    const selectedDonVi = computed(
      () =>
        newDonVi.value.find(
          (item) => item.ma_don_vi === selectedMaDonVi.value
        ) || null
    );

    const selectedDonViName = computed(
      () => selectedDonVi.value?.ten_don_vi || selectedMaDonVi.value
    );

    const canContinueFromPreview = computed(
      () =>
        !isLoadingPreview.value &&
        !previewError.value &&
        Boolean(selectedMaDonVi.value)
    );

    const isGeojsonLocated = computed(
      () =>
        Boolean(geojsonFeature.value?.properties?.located) &&
        geojsonFeature.value?.properties?.latitude != null &&
        geojsonFeature.value?.properties?.longitude != null
    );

    const mapLatitude = computed(() =>
      isGeojsonLocated.value
        ? Number(geojsonFeature.value?.properties?.latitude)
        : null
    );

    const mapLongitude = computed(() =>
      isGeojsonLocated.value
        ? Number(geojsonFeature.value?.properties?.longitude)
        : null
    );

    const showMapPreview = computed(
      () =>
        isGeojsonLocated.value &&
        !isLoadingGeojson.value &&
        mapLatitude.value != null &&
        mapLongitude.value != null
    );

    const mapPreviewKey = computed(
      () => `${mapLatitude.value},${mapLongitude.value}`
    );

    const canSubmitSync = computed(
      () => isGeojsonLocated.value && !isLoadingGeojson.value
    );

    const modalTitle = computed(() => {
      switch (step.value) {
        case "admins":
          return "Tài khoản quản trị mặc định";
        case "geojson":
          return "Xác nhận vị trí đơn vị";
        case "importing":
          return "Đang đồng bộ";
        case "result":
          return "Kết quả đồng bộ";
        default:
          return "Đồng bộ đơn vị từ hệ thống kế toán";
      }
    });

    const resultSummary = computed(() => {
      const data = result.value || {};
      const adminList = data.admins || [];
      const firstDonViId =
        data.don_vi?.[0]?.id ||
        adminList.find((item) => item.id_don_vi)?.id_don_vi ||
        undefined;

      return {
        importedCount: data.imported_count ?? data.don_vi?.length ?? 1,
        syncedUsersCount: data.synced_users_count ?? 0,
        admins: adminList,
        firstDonViId,
      };
    });

    const resetGeojsonState = () => {
      geojsonFeature.value = null;
      geojsonError.value = "";
      isLoadingGeojson.value = false;
    };

    const unloadMapPreview = () => {
      geojsonFeature.value = null;
    };

    const initAddressFromSelection = () => {
      const row = selectedDonVi.value;
      // Preview API: dia_chi → gốc (kế toán), dia_chi_goc → địa chỉ bản đồ
      diaChiGoc.value = (row?.dia_chi || row?.dia_chi_goc || "").trim();
      diaChi.value = (row?.dia_chi_goc || row?.dia_chi || "").trim();
      resetGeojsonState();
    };

    const resetState = () => {
      step.value = "preview";
      filterMaDonVi.value = "";
      newDonVi.value = [];
      selectedMaDonVi.value = "";
      syncNguoiDung.value = true;
      Object.assign(adminDraft, createEmptyAdmin());
      result.value = null;
      previewMessage.value = "";
      previewError.value = "";
      isLoadingPreview.value = false;
      diaChiGoc.value = "";
      diaChi.value = "";
      resetGeojsonState();
    };

    const loadPreview = async () => {
      isLoadingPreview.value = true;
      previewError.value = "";
      previewMessage.value = "";
      newDonVi.value = [];
      selectedMaDonVi.value = "";

      try {
        const { items, message } = await DonViService.fetchNewDonViFromKetoan({
          maDonVi: filterMaDonVi.value || undefined,
        });
        newDonVi.value = items;
        previewMessage.value = message || "";
        if (items.length === 1) {
          selectedMaDonVi.value = items[0].ma_don_vi;
        }
      } catch (error: unknown) {
        const status = (error as { response?: { status?: number } })?.response
          ?.status;
        if (status === 403) {
          previewError.value = "Không có quyền";
          await swalNotification("Không có quyền", "error");
        } else {
          previewError.value = getErrorMsg(
            error,
            "Không tải được danh sách đơn vị mới từ kế toán"
          );
          await swalErrNotification(
            error,
            "Không tải được danh sách đơn vị mới từ kế toán"
          );
        }
      } finally {
        isLoadingPreview.value = false;
      }
    };

    watch(
      () => props.isOpen,
      async (open) => {
        if (open) {
          resetState();
          await loadPreview();
        }
      }
    );

    const onRowClick = (row: IKetoanDonViPreview) => {
      selectedMaDonVi.value = row.ma_don_vi;
    };

    const onDiaChiEdited = () => {
      if (suppressDiaChiUnload.value) {
        return;
      }
      unloadMapPreview();
      geojsonError.value = "";
    };

    const requestGeojsonPreview = async () => {
      if (!selectedMaDonVi.value || !diaChi.value.trim()) {
        await swalNotification("Vui lòng nhập địa chỉ bản đồ", "warning");
        return;
      }

      isLoadingGeojson.value = true;
      unloadMapPreview();
      geojsonError.value = "";

      try {
        const feature = await DonViService.previewDonViGeojson({
          locations: [
            {
              ma_don_vi: selectedMaDonVi.value,
              ten_don_vi: selectedDonViName.value,
              dia_chi_goc: diaChiGoc.value,
              dia_chi: diaChi.value.trim(),
            },
          ],
        });

        if (!feature) {
          geojsonError.value = "Không nhận được dữ liệu GeoJSON từ máy chủ";
          return;
        }

        const isLocated =
          Boolean(feature.properties?.located) &&
          feature.properties?.latitude != null &&
          feature.properties?.longitude != null;

        if (!isLocated) {
          const reason = feature.properties?.reason || "";
          geojsonError.value =
            GEOCODE_REASON_LABELS[reason] ||
            reason ||
            "Không xác định được vị trí từ địa chỉ";
          // Keep feature for error details UI; map stays unloaded.
          geojsonFeature.value = feature;
          return;
        }

        if (
          feature.properties.dia_chi &&
          feature.properties.dia_chi !== diaChi.value
        ) {
          suppressDiaChiUnload.value = true;
          diaChi.value = feature.properties.dia_chi;
          await nextTick();
          suppressDiaChiUnload.value = false;
        }

        geojsonFeature.value = feature;
      } catch (error: unknown) {
        unloadMapPreview();
        const status = (error as { response?: { status?: number } })?.response
          ?.status;
        if (status === 403) {
          geojsonError.value = "Không có quyền";
          await swalNotification("Không có quyền", "error");
        } else {
          geojsonError.value = getErrorMsg(error, "Xem trước vị trí thất bại");
          await swalErrNotification(error, "Xem trước vị trí thất bại");
        }
      } finally {
        isLoadingGeojson.value = false;
        suppressDiaChiUnload.value = false;
      }
    };

    const enterGeojsonStep = async () => {
      step.value = "geojson";
      await nextTick();
      await requestGeojsonPreview();
    };

    const onContinueFromPreview = async () => {
      if (!selectedMaDonVi.value) {
        swalNotification("Vui lòng chọn một đơn vị", "warning");
        return;
      }

      initAddressFromSelection();

      if (!syncNguoiDung.value) {
        Object.assign(adminDraft, createEmptyAdmin(selectedMaDonVi.value));
        step.value = "admins";
        return;
      }

      await enterGeojsonStep();
    };

    const validateAdmin = (): string | null => {
      const hoVaTen = adminDraft.ho_va_ten.trim();
      const tenDangNhap = adminDraft.ten_dang_nhap.trim();
      const matKhau = adminDraft.mat_khau;
      const soDienThoai = adminDraft.so_dien_thoai.trim();
      const email = adminDraft.email?.trim() || "";

      if (!hoVaTen) return "Họ và tên không được để trống";
      if (!tenDangNhap) return "Tên đăng nhập không được để trống";
      if (!matKhau) return "Mật khẩu không được để trống";
      if (matKhau.length < 8 || matKhau.length > 32) {
        return "Mật khẩu phải từ 8 đến 32 ký tự";
      }
      if (!soDienThoai) return "Số điện thoại không được để trống";
      if (email && !EMAIL_PATTERN.test(email)) {
        return "Email không đúng định dạng";
      }

      return null;
    };

    const onContinueFromAdmins = async () => {
      const validationError = validateAdmin();
      if (validationError) {
        await swalNotification(validationError, "warning");
        return;
      }

      adminDraft.ma_don_vi = selectedMaDonVi.value;
      await enterGeojsonStep();
    };

    const onBackFromGeojson = () => {
      step.value = syncNguoiDung.value ? "preview" : "admins";
    };

    const mapFieldErrors = (error: unknown) => {
      const responseData = (
        error as {
          response?: {
            data?: {
              errors?: Record<string, string[] | string>;
              data?: {
                errors?: Record<string, string[] | string>;
                msg?: string;
              };
            };
          };
        }
      )?.response?.data;

      const errors = responseData?.errors || responseData?.data?.errors;
      if (!errors || typeof errors !== "object") {
        return null;
      }

      const messages = Object.values(errors)
        .flatMap((value) => (Array.isArray(value) ? value : [value]))
        .filter(Boolean);

      return messages.length ? messages.join("\n") : null;
    };

    const buildDonViInfo = (): IKetoanDonViInfo | null => {
      const props = geojsonFeature.value?.properties;
      if (!props || !isGeojsonLocated.value) {
        return null;
      }

      return {
        ma_don_vi: selectedMaDonVi.value,
        dia_chi: diaChi.value.trim(),
        dia_chi_goc: diaChiGoc.value,
        latitude: props.latitude ?? null,
        longitude: props.longitude ?? null,
        ...(props.province_code ? { province_code: props.province_code } : {}),
      };
    };

    const onSubmitSync = async () => {
      if (!canSubmitSync.value) {
        await swalNotification(
          "Vui lòng xem trước vị trí thành công trước khi đồng bộ",
          "warning"
        );
        return;
      }

      const donViInfo = buildDonViInfo();
      if (!donViInfo) {
        await swalNotification("Thiếu thông tin vị trí đơn vị", "warning");
        return;
      }

      const payload: IKetoanSyncPayload = {
        ma_don_vi: [selectedMaDonVi.value],
        sync_nguoidung: syncNguoiDung.value,
        don_vi_info: [donViInfo],
      };

      if (!syncNguoiDung.value) {
        payload.admins = [
          {
            ma_don_vi: selectedMaDonVi.value,
            ho_va_ten: adminDraft.ho_va_ten.trim(),
            ten_dang_nhap: adminDraft.ten_dang_nhap.trim(),
            mat_khau: adminDraft.mat_khau,
            so_dien_thoai: adminDraft.so_dien_thoai.trim(),
            ...(adminDraft.email?.trim()
              ? { email: adminDraft.email.trim() }
              : {}),
          },
        ];
      }

      const previousStep = step.value;
      step.value = "importing";

      try {
        const { data } = await DonViService.syncDonViFromKetoan(payload);
        result.value = data.data || {};
        step.value = "result";
      } catch (error: unknown) {
        const status = (error as { response?: { status?: number } })?.response
          ?.status;

        step.value = previousStep;

        if (status === 403) {
          await swalNotification("Không có quyền", "error");
          return;
        }

        if (status === 422) {
          const fieldMsg = mapFieldErrors(error);
          await swalNotification(
            fieldMsg || getErrorMsg(error, "Dữ liệu không hợp lệ"),
            "error"
          );
          return;
        }

        await swalErrNotification(error, "Đồng bộ thất bại. Vui lòng thử lại.");
      }
    };

    const formatCapDonVi = (cap?: number) => {
      if (cap === 1) return "Cty";
      if (cap === 2) return "ĐV";
      return cap != null ? String(cap) : "-";
    };

    const formatCoord = (value?: number | null) =>
      value == null || Number.isNaN(Number(value)) ? "-" : String(value);

    const onClose = () => {
      if (step.value === "importing") {
        return;
      }
      emit("close");
    };

    const onFinish = () => {
      emit("success");
    };

    return {
      ButtonsType,
      ButtonTypeColors,
      ButtonSizeEnums,
      step,
      filterMaDonVi,
      newDonVi,
      selectedMaDonVi,
      selectedDonViName,
      syncNguoiDung,
      adminDraft,
      result,
      previewMessage,
      previewError,
      isLoadingPreview,
      diaChiGoc,
      diaChi,
      geojsonFeature,
      geojsonError,
      isLoadingGeojson,
      mapLatitude,
      mapLongitude,
      showMapPreview,
      mapPreviewKey,
      canContinueFromPreview,
      isGeojsonLocated,
      canSubmitSync,
      modalTitle,
      resultSummary,
      loadPreview,
      onRowClick,
      onContinueFromPreview,
      onContinueFromAdmins,
      onBackFromGeojson,
      onDiaChiEdited,
      requestGeojsonPreview,
      onSubmitSync,
      formatCapDonVi,
      formatCoord,
      onClose,
      onFinish,
    };
  },
});
